/**
 * Composable for managing dual Intercom system
 *
 * This handles the site-wide Intercom integration, switching between
 * general support chat and PawSupport chat based on user context.
 */

// LocalStorage key for tracking active PawSupport chat session
const PAWSUPPORT_SESSION_KEY = 'embrace_pawsupport_active'

// Global state to persist across composable instances
const globalState = {
  currentMode: ref<'general' | 'pawsupport' | 'disabled'>('disabled'),
  isInitialized: ref(false),
  isIntercomAvailable: ref(false),
  generalAppId: null as string | null,
  pawSupportAppId: null as string | null,
  initializationInProgress: false
}

export const useIntercomManager = () => {
  const { currentMode, isInitialized, isIntercomAvailable } = globalState

  const hasActivePawSupportSession = (): boolean => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false
    }
    return localStorage.getItem(PAWSUPPORT_SESSION_KEY) === 'true'
  }

  const setActivePawSupportSession = (): void => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem(PAWSUPPORT_SESSION_KEY, 'true')
    }
  }

  const clearActivePawSupportSession = (): void => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.removeItem(PAWSUPPORT_SESSION_KEY)
    }
  }

  const isIntercomLoaded = (): boolean => {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
      return false
    }

    // Check if it's still the stub function (has .q property)
    const intercom = window.Intercom as any
    return !intercom.q // If there's no queue, it means the real script has loaded
  }

  /**
   * Create Intercom settings object based on mode
   */
  const createIntercomSettings = (
    appId: string,
    mode: 'general' | 'pawsupport',
    userData?: {
      userId: string
      name: string
      email: string
      createdAt?: number
    }
  ) => {
    const settings: any = {
      app_id: appId,
      // IMPORTANT: For PawSupport, we set hide_default_launcher to false to allow
      // the messenger to persist across page refreshes. We hide the launcher button
      // visually with CSS instead (see custom_launcher_selector).
      // For general support, show the launcher normally.
      hide_default_launcher: false,
      // For PawSupport mode, use a custom launcher selector to hide the default button
      // This allows the messenger to appear on refresh but keeps the button hidden
      ...(mode === 'pawsupport' && {
        custom_launcher_selector: '#intercom-custom-launcher-pawsupport'
      })
    }

    // Add user data for both general support and PawSupport modes
    // This allows both workspaces to have pet parent email and portal username
    if (userData) {
      settings.user_id = userData.userId
      settings.name = userData.name
      settings.email = userData.email
      if (userData.createdAt) {
        settings.created_at = userData.createdAt
      }
    }

    return settings
  }

  /**
   * Reset Intercom state to disabled
   */
  const resetIntercomState = () => {
    currentMode.value = 'disabled'
    isInitialized.value = false
    isIntercomAvailable.value = false
    globalState.generalAppId = null
    globalState.pawSupportAppId = null
  }

  /**
   * Get user data for Intercom from the user store
   */
  const getUserDataForIntercom = () => {
    const userStore = useUserStore()

    if (!userStore.isAuthenticated || !userStore.user?.accountOverview) {
      return null
    }

    const { accountOverview } = userStore.user

    // Our understanding is the the created_at field will not affect the Intercom workflows we have set up
    // Intercom workflows are set up via Intercom's dashboard UI, which the business has ownership of, not development
    return {
      userId: accountOverview.username || accountOverview.emailAddress,
      name: `${accountOverview.firstName} ${accountOverview.lastName}`.trim() || accountOverview.username,
      email: accountOverview.emailAddress,
      createdAt: Math.floor(Date.now() / 1000) // Use current time as placeholder
    }
  }

  /**
   * Load Intercom script with specified app ID
   */
  const loadIntercomScript = async (appId: string): Promise<void> => {
    if (window.loadIntercomScript) {
      await window.loadIntercomScript(appId)
    } else {
      throw new Error('Intercom script loader not available')
    }

    // Wait for Intercom to be fully loaded
    const maxWaitTime = 15000 // 15 seconds
    const checkInterval = 200 // Check every 200ms
    const maxAttempts = maxWaitTime / checkInterval

    let attempts = 0
    let scriptLoaded = false

    while (!scriptLoaded && attempts < maxAttempts) {
      if (isIntercomLoaded()) {
        scriptLoaded = true
      }

      if (!scriptLoaded) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval))
        attempts++
      }
    }

    if (!scriptLoaded) {
      throw new Error(`Intercom failed to load after ${maxWaitTime}ms`)
    }
  }

  /**
   * Switch Intercom to specified mode
   */
  const switchMode = async (mode: 'general' | 'pawsupport'): Promise<boolean> => {
    try {
      // Prevent multiple concurrent mode switches
      if (globalState.initializationInProgress) {
        return true // Already in progress, consider this success
      }

      globalState.initializationInProgress = true

      if (!isIntercomAvailable.value) {
        return false
      }

      const targetAppId = mode === 'general' ? globalState.generalAppId : globalState.pawSupportAppId

      if (!targetAppId) {
        return false
      }

      // Get user data for Intercom
      const userData = getUserDataForIntercom()

      // If we're already in the correct mode, nothing to do
      if (currentMode.value === mode && isIntercomLoaded()) {
        globalState.initializationInProgress = false
        return true
      }

      // If Intercom is loaded and app ID has changed, switch modes
      if (isIntercomLoaded() && window.intercomSettings?.app_id !== targetAppId) {
        // If leaving PawSupport mode, clear the session flag
        if (currentMode.value === 'pawsupport' && mode !== 'pawsupport') {
          clearActivePawSupportSession()
        }
        
        // Shutdown current instance to clear session data
        window.Intercom('shutdown')

        // Update settings with new app ID
        // For PawSupport: anonymous mode (no user data)
        // For general: authenticated mode (with user data)
        window.intercomSettings = createIntercomSettings(targetAppId, mode, userData || undefined)

        // Boot new instance with new settings
        // Note: We must use 'boot' after shutdown when switching between different app IDs
        // especially when switching between authenticated and anonymous modes
        window.Intercom('boot', window.intercomSettings)
        
        // For PawSupport mode, check if there's an active session
        if (mode === 'pawsupport') {
          const hasActiveSession = hasActivePawSupportSession()
          
          if (hasActiveSession) {
            // User has an active session, show the messenger
            window.Intercom('show')
          } else {
            // No active session, hide launcher until user accepts terms
            window.Intercom('update', { hide_default_launcher: true })
          }
        }
      } else if (!isIntercomLoaded()) {
        // Only load new script if Intercom doesn't exist
        await loadIntercomScript(targetAppId)

        // Configure initial settings
        window.intercomSettings = createIntercomSettings(targetAppId, mode, userData || undefined)

        // Boot Intercom for first time
        window.Intercom('boot', window.intercomSettings)
        
        // Setup Intercom event listeners
        setupIntercomEventListeners()
        
        // For PawSupport mode, check if there's an active session
        if (mode === 'pawsupport') {
          const hasActiveSession = hasActivePawSupportSession()
          
          if (hasActiveSession) {
            // User has an active session, show the messenger
            window.Intercom('show')
          } else {
            // No active session, hide launcher until user accepts terms
            window.Intercom('update', { hide_default_launcher: true })
          }
        }
      }

      // Update the current mode state
      currentMode.value = mode
      return true
    } catch {
      return false
    } finally {
      // Always reset the initialization flag
      globalState.initializationInProgress = false
    }
  }

  /**
   * Initialize Intercom system after login
   * Fetches app IDs and sets up in the appropriate mode based on current route
   */
  const initializeAfterLogin = async (): Promise<void> => {
    try {
      // Prevent duplicate initialization
      if (isInitialized.value || currentMode.value !== 'disabled') {
        return
      }

      // Check if user is authenticated
      const userStore = useUserStore()
      if (!userStore.isAuthenticated) {
        throw new Error('User is not authenticated')
      }

      // Fetch both app IDs
      const { getIntercomAppIds } = useIntercomConfig()
      const { generalAppId: fetchedGeneralAppId, pawSupportAppId: fetchedPawSupportAppId } = await getIntercomAppIds()

      globalState.generalAppId = fetchedGeneralAppId
      globalState.pawSupportAppId = fetchedPawSupportAppId

      isIntercomAvailable.value = true

      // Check current route and initialize in the appropriate mode
      const route = useRoute()
      const { isPawSupportRoute } = useIntercomNavigation()
      const initialMode = isPawSupportRoute(route.path) ? 'pawsupport' : 'general'
      
      const success = await switchMode(initialMode)

      if (success) {
        isInitialized.value = true
      } else {
        // Failed to switch mode - reset state
        resetIntercomState()
      }
    } catch {
      // Disable Intercom on failure
      resetIntercomState()

      // Don't show error toast here - this is background initialization
      // Error will be shown when user tries to use chat
    }
  }

  const setupIntercomEventListeners = (): void => {
    if (!isIntercomLoaded()) {
      return
    }

    // Listen for when the messenger is hidden
    window.Intercom('onHide', () => {
      if (currentMode.value === 'pawsupport') {
        clearActivePawSupportSession()
        // Hide the launcher again since they closed the chat
        window.Intercom('update', { hide_default_launcher: true })
      }
    })

    // Listen for when the messenger is shown
    window.Intercom('onShow', () => {
      if (currentMode.value === 'pawsupport') {
        setActivePawSupportSession()
      }
    })
  }

  /**
   * Manually show PawSupport chat
   * Call this when user clicks PawSupport button
   */
  const showPawSupportChat = (): void => {
    if (isIntercomLoaded()) {
      // First, enable the launcher so the messenger can appear
      window.Intercom('update', { hide_default_launcher: false })
      
      // Then show the messenger
      window.Intercom('show')
      
      // Mark session as active (also handled by onShow event, but set here as backup)
      setActivePawSupportSession()
    }
  }

  /**
   * Shutdown Intercom and clear cache
   * Called during logout
   */
  const shutdown = (): void => {
    try {
      if (isIntercomLoaded()) {
        window.Intercom('shutdown')
      }

      // Clear PawSupport session flag
      clearActivePawSupportSession()

      // Reset state
      resetIntercomState()

      // Clear cached configuration
      const { clearCache } = useIntercomConfig()
      clearCache()
    } catch {
      // Fail silently
    }
  }

  return {
    // State
    currentMode: readonly(currentMode),
    isInitialized: readonly(isInitialized),
    isIntercomAvailable: readonly(isIntercomAvailable),

    // Methods
    initializeAfterLogin,
    switchMode,
    showPawSupportChat,
    shutdown
  }
}
